import { uniqBy } from "lodash-es";
import { ALL_TO_UPPERCASE_MAP, METRIC_DISPLAY_NAME } from "../pages/Agent/config/constant";
import { mergeData } from "./utils";

// 输入数据类型定义
interface MetricValue {
  source: string;
  date: string;
  market_name: string;
  platform?: string;
  [key: string]: any; // 允许动态指标字段
}

interface GameData {
  game_name: string;
  game_type: string;
  metric_value: MetricValue[];
  data?: {
    data: GameData[];
  };
  title?: string;
}

// 新增：表格数据类型定义
interface TableDataItem {
  date: string;
  game_name: string;
  entity_type: string;
  region_name: string;
  source: string;
  revenue?: string;
  pcu?: string;
  digitalrevenue?: string;
  est_revenue?: string;
  [key: string]: any; // 允许动态字段
}

interface ColumnInfo {
  name: string;
  name_en: string;
  origin_name: string;
  can_sort: boolean;
  type: number;
  column_date_type: string;
  accuracy: any;
  table_code: string;
}

interface TableInfo {
  data: TableDataItem[];
  columnsInfo: ColumnInfo[];
}

// 输出数据类型定义
interface LegendIcon {
  game_type: string;
  img_url: string;
}

interface Legend {
  compare_column: string[];
  compare_name: string;
  icon?: LegendIcon[];
  legend_column: string[];
  data: string[];
}

interface ChartDataItem {
  date: string;
  entity_type: string;
  game_name: string;
  region_name: string;
  platform?: string;
  source: string;
  [key: string]: any; // 允许动态指标字段
}

interface EntitySourceMetric {
  value: string;
  text: string;
  x_key: string;
  column: string;
  column_type: string;
  icon: string;
  disabled?: boolean;
}

interface InfoDisplay {
  column: string;
  display: string[];
  x_key: string;
}

interface TransformResult {
  legends: Legend[];
  chartData: ChartDataItem[];
  chartTitle: string;
  chartTypes: string[];
  entities: EntitySourceMetric[];
  sources: EntitySourceMetric[];
  metrics: EntitySourceMetric[];
  entityInfos: Record<string, InfoDisplay>;
  sourceInfos: Record<string, InfoDisplay>;
  metricInfos: Record<string, InfoDisplay>;
  tableInfo?: {
    data: ChartDataItem[];
    columnsInfo: ColumnInfo[];
  };
}

function transformGameData(inputData: GameData[]) {
  if (inputData?.[0]?.data?.data) {
    const realData: any = [];
    inputData.forEach(item => {
      realData.push(...(item?.data?.data || []));
    });
    return fromatData(mergeData(inputData, realData), inputData?.[0]?.title);
  }
  return fromatData(inputData);
}

function fromatData(inputData: GameData[], title?: string): TransformResult | null {
  if (!Array.isArray(inputData) || inputData.length === 0) {
    return null;
  }

  const result: TransformResult = {
    legends: [],
    chartData: [],
    chartTitle: '',
    chartTypes: ['line', 'bar', 'table'],
    entities: [],
    sources: [],
    metrics: [],
    entityInfos: {},
    sourceInfos: {},
    metricInfos: {},
  };
  const gameSet = new Set<string>();
  // 用于聚合的集合
  const gameTypesSet = new Set<string>();
  const sourcesSet = new Set<string>();
  const marketsSet = new Set<string>();
  const metricsSet = new Set<string>();
  const platformSet = new Set<string>();

  // 处理每个游戏数据
  inputData.forEach(gameItem => {
    // eslint-disable-next-line @typescript-eslint/naming-convention, prefer-const
    let { game_name, game_type, metric_value, metric_value: list } = gameItem || {};
    // 判断是否有device，如果有则转device为platform，没有则不用map转换
    const hasDeviceKey = hasDevice(list);
    metric_value = !hasDeviceKey ? list : list?.map(item => ({ ...item, platform: item?.platform || item?.device, }));
    // 过滤掉没有date的数据，目前不支持非date的数据
    metric_value = metric_value?.filter(item => item.date) || [];
    // 收集所有唯一的game_type
    gameTypesSet.add(game_type);
    gameSet.add(game_name);

    const chartData: ChartDataItem[] = [];

    // 处理legends
    // const markets = [...new Set(metric_value.map(item => item.market_name))];
    const sources = [...new Set(metric_value?.map(item => item.source))];
    const platforms = metric_value?.map(item => item.platform) || [];
    platforms.forEach(platform => {
      if (!platform) {
        return;
      }
      platformSet.add(platform);
    });
    const sourceMap = {} as any;
    const metricMap = {} as any;

    // result.legends.push({
    //   compare_column: ['game_name'],
    //   compare_name: game_name,
    //   icon: [{
    //     game_type: game_type,
    //     img_url: '' // 默认为空，可根据需要填充
    //   }],
    //   legend_column: ['region_name'],
    //   data: markets
    // });

    const innerMetrics = new Set<string>();

    // 处理chartData
    metric_value?.forEach(metricItem => {
      // eslint-disable-next-line prefer-const
      let { source, date, market_name, region_name, platform, device, ...metrics } = metricItem;
      // 收集sources和markets
      sourcesSet.add(source);
      marketsSet.add(market_name || region_name);

      if (!platform && platformSet.size > 0) {
        platform = 'ALL';
        platformSet.add(platform);
        // return;
      }

      // 动态收集所有指标字段
      Object.keys(metrics).forEach(metricKey => {
        metricsSet.add(metricKey);
        innerMetrics.add(metricKey);

        if (source) {
          if (!sourceMap[source]) {
            sourceMap[source] = new Set<string>();
          }
          sourceMap[source].add(metricKey);
          if (!metricMap[metricKey]) {
            metricMap[metricKey] = new Set<string>();
          }
          metricMap[metricKey].add(source);
        }
      });

      // 创建chartData项，包含所有指标
      const chartDataItem: ChartDataItem = {
        date,
        entity_type: game_type,
        game_name,
        region_name: market_name || region_name,
        platform,
        source
      };

      // 添加所有指标字段
      Object.keys(metrics).forEach(metricKey => {
        chartDataItem[metricKey] = metrics[metricKey];
      });

      // result.chartData.push(chartDataItem);
      chartData.push(chartDataItem);
    });

    result.entityInfos[game_type] = {
      column: 'entity_type',
      display: [...sources, ...innerMetrics],
      x_key: ''
    };

    sources?.forEach(source => {
      if (result.sourceInfos[source]) {
        result.sourceInfos[source].display.push(game_type, ...(sourceMap[source] || []));
        // 去重display数组
        result.sourceInfos[source].display = [...new Set(result.sourceInfos[source].display)];
      } else {
        result.sourceInfos[source] = {
          column: 'source',
          display: [game_type, ...(sourceMap[source] || [])],
          x_key: ''
        };
      }
    });

    innerMetrics?.forEach(metric => {
      const availableSources = metricMap[metric] || [];
      const availableEntities = [game_type];
      const oldDisplay = result.metricInfos[metric]?.display || [];

      result.metricInfos[metric] = {
        column: metric,
        display: [...new Set([...oldDisplay, ...availableEntities, ...availableSources])],
        x_key: 'date'
      };

      if (!chartData?.[0]?.[metric]) {
        chartData[0][metric] = undefined;
      }
    });

    result.chartData = [...result.chartData, ...chartData];
  });

  const isPlatform = platformSet.size > 0;

  gameSet.forEach(game => {
    result.legends.push({
      compare_column: ['game_name'],
      compare_name: game,
      icon: undefined,
      legend_column: isPlatform ? ['region_name', 'platform'] : ['region_name'],

      // data: [...marketsSet]
      data: [...marketsSet].map(market => {
        if (isPlatform) {
          return [...platformSet].map(platform => `${market}$~${platform}`);
        }
        return market;
      }).flat()
    });
  });

  // 生成entities (基于game_type聚合)
  gameTypesSet.forEach(gameType => {
    result.entities.push({
      value: gameType,
      text: ALL_TO_UPPERCASE_MAP?.[gameType?.toLowerCase()] || (gameType?.charAt?.(0).toUpperCase() + gameType.slice(1)),
      x_key: '',
      column: 'entity_type',
      column_type: '',
      icon: ''
    });
  });

  // 生成sources (基于metric_value中的source聚合)
  sourcesSet.forEach(source => {
    result.sources.push({
      value: source,
      text: source.charAt(0).toUpperCase() + source.slice(1),
      x_key: '',
      column: 'source',
      column_type: '',
      icon: '',
      disabled: false
    });
  });

  // 生成metrics
  metricsSet.forEach(metric => {
    // if (['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9', 'D10', 'D11', 'D12', 'D13', 'D14', 'D15', 'D16', 'D17', 'D18', 'D19', 'D20', 'D21', 'D22', 'D23', 'D24', 'D25', 'D26', 'D27', 'D28', 'D29', 'D30', 'D31', 'D60', 'D90'].includes(metric)) {
    //     return;
    // }
    result.metrics.push({
      value: metric,
      text: getMetricDisplayName(metric),
      x_key: 'date',
      column: metric,
      column_type: 'number',
      icon: '',
      disabled: false
    });
  });

  // 生成entityInfos
  //   gameTypesSet.forEach(gameType => {
  //     const availableSources = [...sourcesSet];
  //     const availableMetrics = [...metricsSet];

  //     result.entityInfos[gameType] = {
  //       column: 'entity_type',
  //       display: [...availableSources, ...availableMetrics],
  //       x_key: ''
  //     };
  //   });

  // 生成sourceInfos
  // sourcesSet.forEach(source => {
  //   const availableEntities = [...gameTypesSet];
  //   const availableMetrics = [...metricsSet];

  //   result.sourceInfos[source] = {
  //     column: 'source',
  //     display: [...availableEntities, ...availableMetrics],
  //     x_key: ''
  //   };
  // });

  // 生成metricInfos
  // metricsSet.forEach(metric => {
  //   const availableSources = [...sourcesSet];
  //   const availableEntities = [...gameTypesSet];

  //   result.metricInfos[metric] = {
  //     column: metric,
  //     display: [...availableSources, ...availableEntities],
  //     x_key: 'date'
  //   };
  // });

  // 生成chartTitle (可根据需要自定义)
  const gameNames = inputData.map(item => item.game_name).join(', ');
  const dateRange = getDateRange(result.chartData);
  result.chartTitle = title || `${gameNames} 数据分析 (${dateRange})`;

  // 添加tableInfo - 为嵌套数据格式生成columnsInfo
  const generatedColumnsInfo: ColumnInfo[] = [
    {
      name: '日期',
      name_en: 'date',
      origin_name: 'date',
      can_sort: false,
      type: 0,
      column_date_type: 'string',
      accuracy: null,
      table_code: ''
    },
    {
      name: '游戏名',
      name_en: 'game_name',
      origin_name: 'game_name',
      can_sort: false,
      type: 0,
      column_date_type: 'string',
      accuracy: null,
      table_code: ''
    },
    {
      name: '实体类型',
      name_en: 'entity_type',
      origin_name: 'entity_type',
      can_sort: false,
      type: 0,
      column_date_type: 'string',
      accuracy: null,
      table_code: ''
    },
    {
      name: '区域',
      name_en: 'region_name',
      origin_name: 'region_name',
      can_sort: false,
      type: 0,
      column_date_type: 'string',
      accuracy: null,
      table_code: ''
    },
    {
      name: '数据源',
      name_en: 'source',
      origin_name: 'source',
      can_sort: false,
      type: 0,
      column_date_type: 'string',
      accuracy: null,
      table_code: ''
    }
  ];

  if (isPlatform) {
    generatedColumnsInfo.push({
      name: '平台',
      name_en: 'platform',
      origin_name: 'platform',
      can_sort: false,
      type: 0,
      column_date_type: 'string',
      accuracy: null,
      table_code: ''
    });
  }

  // 为动态指标字段生成columnsInfo
  metricsSet.forEach(metric => {
    generatedColumnsInfo.push({
      name: getMetricDisplayName(metric),
      name_en: metric,
      origin_name: metric,
      can_sort: false,
      type: 1,
      column_date_type: 'number',
      accuracy: null,
      table_code: ''
    });
  });

  result.tableInfo = {
    data: result.chartData,
    columnsInfo: uniqBy(generatedColumnsInfo, 'origin_name')
  };

  return result;
}

export function hasDevice(list: any[]): boolean {
  // for (const item of list) {
  //   if (item?.metric_value && Array.isArray(item.metric_value)) {
  for (const metric of list) {
    if (metric?.device !== undefined) {
      return true; // 找到第一个包含 device 的项后立即返回
    }
    //   }
    // }
  }
  return false;
}

// 辅助函数：获取指标显示名称
export function getMetricDisplayName(metric: string): string {
  const metricDisplayNames: Record<string, string> = METRIC_DISPLAY_NAME;

  return metricDisplayNames?.[metric] || metric?.replace(/_/g, ' ').replace(/\b\w/g, l => l?.toUpperCase());
}

// 辅助函数：获取日期范围
function getDateRange(chartData: ChartDataItem[]): string {
  if (chartData.length === 0) return '';

  const dates = chartData.map(item => item.date).sort();
  const startDate = dates[0];
  const endDate = dates[dates.length - 1];

  return startDate === endDate ? startDate : `${startDate} ~ ${endDate}`;
}

// 导出函数和类型
export {
  transformGameData,
  type GameData,
  type TransformResult,
  type MetricValue,
  type ChartDataItem,
  type TableDataItem,
  type TableInfo,
  type ColumnInfo
};
export default transformGameData;

// // 示例使用
// const sampleData: GameData[] = [
//   {
//     "game_name": "Genshin Impact",
//     "game_type": "console",
//     "metric_value": [
//       {
//         "source": "ampere",
//         "date": "2024-01-01",
//         "market_name": "Global",
//         "ampere_mau": 2556106
//       },
//       {
//         "source": "ampere",
//         "date": "2024-02-01",
//         "market_name": "Global",
//         "ampere_mau": 2481118
//       }
//       // ... 更多数据
//     ]
//   }
// ];

// 使用示例
// const transformedData = transformGameData(sampleData);
// console.log(JSON.stringify(transformedData, null, 2));

// 新增：表格数据使用示例
// const tableData: TableInfo = {
//   data: [
//     {
//       "date": "2025-04",
//       "digitalrevenue": "",
//       "entity_type": "pc",
//       "est_revenue": "1447753",
//       "game_name": "Deep Rock Galactic",
//       "pcu": "",
//       "region_name": "Global",
//       "revenue": "",
//       "source": "vginsights"
//     }
//     // ... 更多数据
//   ],
//   columnsInfo: [
//     {
//       "name": "日期",
//       "name_en": "date",
//       "origin_name": "date",
//       "can_sort": false,
//       "type": 0,
//       "column_date_type": "string",
//       "accuracy": null,
//       "table_code": ""
//     }
//     // ... 更多列信息
//   ]
// };

// 使用统一的转换函数 - 自动识别数据格式
// const transformedData = transformData(sampleData); // GameData[] 格式
// const transformedTableData = transformData(tableData); // TableInfo 格式
// console.log(JSON.stringify(transformedData, null, 2)); 